// -----------------------------------------------------------------------------
// LogCallPanel  (Wave A)
//
// Action panel body for PHONE_CALLED / LOG_PHONE_CALL.
// Fields: outcome (answered / no-answer / voicemail), duration (minutes),
//         notes (textarea).
// Submit: POST a note with contacted_via='PHONE', then call onComplete.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Stack,
} from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

const OUTCOMES = [
    { value: 'answered',  label: 'Answered' },
    { value: 'no-answer', label: 'No answer' },
    { value: 'voicemail', label: 'Voicemail' },
];

export default function LogCallPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [outcome, setOutcome] = useState('answered');
    const [duration, setDuration] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
            const lines = [
                `Call outcome: ${outcome}`,
                duration ? `Duration: ${duration} min` : '',
                notes,
            ].filter(Boolean);
            const additionalNotes = lines.join('\n');

            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                'post',
                {
                    lead: leadId,
                    // LeadNote API stores free text in `additional_notes`; `description` is
                    // ignored by the serializer (was silently dropping the call notes).
                    additional_notes: additionalNotes,
                    contacted_via: 'PHONE',
                    ...(treatmentId ? { treatment: treatmentId } : {}),
                }
            );

            if (res.status === 201) {
                dispatch(updateError({ success: true, message: 'Call logged' }));
                onComplete?.(item);
            } else {
                const msg = res?.data?.detail || res?.data?.additional_notes?.[0] || 'Failed to log call';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="log-call-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(52, 199, 89, 0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:phone" width={18} height={18} sx={{ color: '#34c759' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                        Log call
                    </Typography>
                    {leadDetail?.first_name && (
                        <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                            {[leadDetail.first_name, leadDetail.last_name].filter(Boolean).join(' ')}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Outcome picker */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Call outcome
                </Typography>
                <RadioGroup
                    data-testid="log-call-outcome"
                    row
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                >
                    {OUTCOMES.map(({ value, label }) => (
                        <FormControlLabel
                            key={value}
                            value={value}
                            control={<Radio size="small" sx={{ color: '#6e6e73', '&.Mui-checked': { color: '#078DEE' } }} />}
                            label={<Typography sx={{ fontSize: '13px', color: '#1d1d1f' }}>{label}</Typography>}
                        />
                    ))}
                </RadioGroup>
            </Box>

            {/* Duration */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Duration (minutes)
                </Typography>
                <TextField
                    data-testid="log-call-duration"
                    type="number"
                    size="small"
                    placeholder="e.g. 5"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    inputProps={{ min: 0, max: 999 }}
                    sx={{ width: 120 }}
                />
            </Box>

            {/* Notes */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Notes
                </Typography>
                <TextField
                    data-testid="log-call-notes"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    placeholder="What was discussed?"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{
                        '& .MuiInputBase-root': { alignItems: 'flex-start' },
                        '& textarea': { resize: 'vertical', minHeight: '40px' },
                    }}
                />
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                <Button
                    data-testid="log-call-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:phone" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#34c759',
                        '&:hover': { bgcolor: '#2db14e' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Logging…' : 'Log call'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onClose?.()}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

LogCallPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

LogCallPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
